/* Dashboard screen */
const { money, fmtDate, fmtDateTime, fmtDateLong } = window.AC;

function Dashboard({ db, year, setYear, onNav }) {
  const [ledgerFilter, setLedgerFilter] = React.useState('all');
  const cf = MODEL.cashflow(db);
  const pnl = MODEL.monthlyPnL(db, year);
  const rec = MODEL.receivables(db);
  const led = MODEL.ledger(db, year);
  const curMonth = new Intl.DateTimeFormat('en', { month: 'long' }).format(new Date(MODEL.TODAY + 'T00:00:00'));

  const ytd = pnl.filter((p) => !p.future).reduce((a, p) => ({ income: a.income + p.income, expense: a.expense + p.expense, billed: a.billed + p.billed }), { income: 0, expense: 0, billed: 0 });
  const chartMax = Math.max(1, ...pnl.map((p) => Math.max(p.income, p.expense)));

  const moves = led.moves.filter((m) => ledgerFilter === 'all' ? true : ledgerFilter === 'in' ? m.type === 'in' : m.type === 'out');
  const movesDesc = [...moves].reverse();
  const inTotal = led.moves.filter((m) => m.type === 'in').reduce((s, m) => s + m.amount, 0);
  const outTotal = led.moves.filter((m) => m.type === 'out').reduce((s, m) => s + m.amount, 0);

  const agingSegs = [rec.buckets.current, rec.buckets.b30, rec.buckets.b60, rec.buckets.b90]
    .map((b) => ({ value: b.amount, color: b.color, label: b.label, short: money(b.amount) }));
  const recList = [...rec.buckets.b90.items, ...rec.buckets.b60.items, ...rec.buckets.b30.items, ...rec.buckets.current.items];

  return (
    <div>
      {/* KPI strip */}
      <div className="kpis">
        <KPI label="Cash on hand" icon="wallet" tone="" value={money(cf.cashBalance)} delta={<span><Icon name="bank" size={12} /> Hanseatic Bank</span>} />
        <KPI label="Outstanding" icon="invoices" value={money(cf.outstanding)} delta={`${rec.buckets.current.items.length + rec.buckets.b30.items.length + rec.buckets.b60.items.length + rec.buckets.b90.items.length} open invoices`} />
        <KPI label="Overdue" icon="alert" tone={cf.overdue > 0 ? 'warn' : ''} value={money(cf.overdue)} delta={<span style={{ color: 'var(--warn)' }}>{rec.buckets.b30.items.length + rec.buckets.b60.items.length + rec.buckets.b90.items.length} past due</span>} />
        <KPI label={`Income · ${curMonth}`} icon="arrowDown" tone="pos" value={money(cf.incomeThisMonth)} delta={<span className="pos"><Icon name="trendUp" size={12} /> received MTD</span>} />
        <KPI label={`Net · ${curMonth}`} icon="trendUp" tone={cf.netThisMonth >= 0 ? 'pos' : 'neg'} value={money(cf.netThisMonth)} delta={`Spend ${money(cf.expenseThisMonth)}`} />
      </div>

      {/* P&L + Receivables */}
      <div className="dash-cols">
        <Panel title="Profit & Loss" sub={`Billed vs. received vs. operating spend · ${year}`} actions={<YearStepper year={year} onChange={setYear} />} flush>
          <div style={{ padding: '14px 14px 6px' }}>
            <div className="pnl-chart">
              {pnl.map((p) => (
                <div key={p.month} className={`pnl-col ${p.future ? 'future' : ''}`} title={`${p.name}: +${money(p.income)} / -${money(p.expense)}`}>
                  <div className="pnl-bars">
                    <div className="pnl-bar inc" style={{ height: `${(p.income / chartMax) * 100}%` }} />
                    <div className="pnl-bar exp" style={{ height: `${(p.expense / chartMax) * 100}%` }} />
                  </div>
                  <span className="mlabel">{p.name[0]}</span>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 8, fontSize: 11, color: 'var(--muted)' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 9, height: 9, borderRadius: 2, background: 'var(--pos)' }} /> Income received</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 9, height: 9, borderRadius: 2, background: 'var(--neg)', opacity: .82 }} /> Expenses</span>
            </div>
          </div>
          <DataTable
            rows={pnl}
            rowKey={(p) => p.month}
            getRowClassName={(p) => p.future ? 'subtle' : ''}
            columns={[
              { header: 'Month', className: 't-strong', render: (p) => <React.Fragment>{p.name} {p.future && <span className="faint" style={{ fontWeight: 400, fontSize: 10 }}>· forecast</span>}</React.Fragment> },
              { header: 'Billed', className: 'num muted', render: (p) => p.billed ? money(p.billed) : '—' },
              { header: 'Income', className: 'num pos', render: (p) => p.income ? money(p.income) : '—' },
              { header: 'Expenses', className: 'num neg', render: (p) => p.expense ? '–' + money(p.expense).replace('-', '') : '—' },
              { header: 'Net', className: (p) => `num ${p.net >= 0 ? 'pos' : 'neg'}`, cellStyle: { fontWeight: 600 }, render: (p) => money(p.net) },
            ]}
            footer={<tr><td>YTD total</td><td className="num">{money(ytd.billed)}</td><td className="num pos">{money(ytd.income)}</td><td className="num neg">–{money(ytd.expense).replace('-', '')}</td><td className={`num ${ytd.income - ytd.expense >= 0 ? 'pos' : 'neg'}`}>{money(ytd.income - ytd.expense)}</td></tr>}
          />
        </Panel>

        <Panel title="Receivables" sub={`${money(rec.total)} outstanding`} actions={<button className="btn sm ghost" onClick={() => onNav('invoices')}>All invoices</button>} flush>
          <div style={{ padding: '14px 14px 6px' }}>
            <StackBar segments={agingSegs} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '7px 14px', marginTop: 12 }}>
              {[rec.buckets.current, rec.buckets.b30, rec.buckets.b60, rec.buckets.b90].map((b, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11.5, whiteSpace: 'nowrap' }}>
                  <span style={{ width: 9, height: 9, borderRadius: 2, background: b.color, flex: 'none' }} />
                  <span className="muted">{b.label}</span>
                  <span className="mono" style={{ marginLeft: 'auto', fontWeight: 500 }}>{money(b.amount)}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="divider" style={{ margin: '8px 0 0' }} />
          <DataTable
            rows={recList}
            rowKey={(e) => e.invoice.id}
            maxHeight={280}
            onRowClick={() => onNav('invoices')}
            columns={[
              { header: 'Customer', render: (e) => <div className="cellstack"><span className="t-strong">{e.customer ? e.customer.name : '—'}</span><span className="t-sub mono">{e.invoice.number}</span></div> },
              { header: 'Aged', className: 'num', render: (e) => e.overdueDays > 0 ? <span className="neg">{e.overdueDays}d</span> : <span className="muted">due {Math.abs(e.overdueDays)}d</span> },
              { header: 'Outstanding', className: 'num t-strong', render: (e) => money(e.totals.outstanding) },
            ]}
          />
        </Panel>
      </div>

      {/* Ledger feed */}
      <Panel
        title="Cash ledger"
        sub={`Payments in & operating spend · ${year}`}
        actions={<React.Fragment>
          <span className="mono" style={{ fontSize: 11.5, color: 'var(--muted)', marginRight: 4 }}>
            <span className="pos">+{money(inTotal)}</span> · <span className="neg">{money(outTotal)}</span>
          </span>
          <Segmented value={ledgerFilter} onChange={setLedgerFilter} options={[{ value: 'all', label: 'All' }, { value: 'in', label: 'Money in' }, { value: 'out', label: 'Money out' }]} />
        </React.Fragment>}
        flush
      >
        <DataTable
          rows={movesDesc}
          rowKey={(m, i) => i}
          maxHeight={420}
          columns={[
            { header: 'Date', style: { width: 78 }, className: 'mono muted', cellStyle: { fontSize: 11.5 }, render: (m) => fmtDate(m.date) },
            { header: 'Type', style: { width: 92 }, render: (m) => m.type === 'in' ? <span className="pill paid sm"><span className="dot" />In</span> : <span className={`pill ${m.wage ? 'partial' : 'overdue'} sm`}><span className="dot" />{m.wage ? 'Wage' : 'Out'}</span> },
            { header: 'Description', render: (m) => <div className="cellstack"><span className="t-strong">{m.note}</span><span className="t-sub">{m.category}</span></div> },
            { header: 'Counterparty', className: 'muted', render: (m) => m.party },
            { header: 'Money in', className: 'num pos', render: (m) => m.type === 'in' ? money(m.amount) : '' },
            { header: 'Money out', className: 'num neg', render: (m) => m.type === 'out' ? money(Math.abs(m.amount)) : '' },
            { header: 'Balance', className: 'num mono', cellStyle: { color: 'var(--ink-2)' }, render: (m) => money(m.balance) },
          ]}
        />
      </Panel>
    </div>
  );
}

const ACTION_ITEM_DETAIL_CONFIG = {
  ticket: { label: 'Ticket', icon: 'check', tone: 'ticket', headline: 'Project board ticket', detailLabel: 'Board context', editFields: ['title', 'status', 'done'] },
  'project-email': { label: 'Project email', icon: 'mail', tone: 'mail', headline: 'Project email requiring review', detailLabel: 'Sender or preview', editFields: ['status'] },
  'email-message': { label: 'Mailbox message', icon: 'inbox', tone: 'mail', headline: 'Mailbox message requiring review', detailLabel: 'Sender or preview', editFields: ['status'] },
  'meeting-followup': { label: 'Meeting follow-up', icon: 'flag', tone: 'followup', headline: 'Meeting transcript needs follow-up extraction', detailLabel: 'Meeting type', editFields: [] },
  note: { label: 'Note', icon: 'note', tone: 'note', headline: 'Project context note', detailLabel: 'Note body', editFields: [] },
  phone: { label: 'Call', icon: 'phone', tone: 'note', headline: 'Project call note', detailLabel: 'Call details', editFields: [] },
  email: { label: 'Project email', icon: 'mail', tone: 'mail', headline: 'Project context email', detailLabel: 'Preview', editFields: [] },
  file: { label: 'File', icon: 'doc', tone: 'default', headline: 'Project context file', detailLabel: 'Description', editFields: [] },
  followup: { label: 'Follow-up', icon: 'calendar', tone: 'followup', headline: 'Project meeting follow-up', detailLabel: 'Meeting context', editFields: [] },
};
const ACTION_ITEM_STATUS_OPTIONS = ['unprocessed', 'awaiting reply', 'processed'];
const TICKET_STATUS_OPTIONS = ['Open', 'In progress', 'Partially done', 'Ready for review', 'Missing info', 'Blocked', 'Done'];
const FOLLOWUP_TYPE_OPTIONS = ['Action', 'Decision', 'Risk', 'Question', 'Remark'];

function actionItemMeta(item, config = ACTION_ITEM_DETAIL_CONFIG) {
  const metas = { ...ACTION_ITEM_DETAIL_CONFIG, ...(config || {}) };
  return metas[item && item.type] || { label: 'Action item', icon: 'bell', tone: 'default', headline: 'Open action item', detailLabel: 'Details' };
}

function actionItemSourceLabel(item) {
  if (!item) return 'Workspace';
  if (item.type === 'ticket') return 'Project board';
  if (item.type === 'project-email') return 'Project email';
  if (item.type === 'email-message') return 'Mailbox';
  if (item.type === 'meeting-followup') return 'Meeting transcript';
  if (item.type === 'file') return 'Project file';
  if (item.type === 'email') return 'Project email';
  if (item.type === 'note' || item.type === 'phone') return 'Project note';
  if (item.type === 'followup') return 'Meeting follow-up';
  return 'Workspace';
}

function actionItemSource(db, item, events) {
  const projects = (db && db.projects) || [];
  const customers = (db && db.customers) || [];
  const calendarEvents = events || (db && db.calendarEvents) || [];
  const meeting = item && item.type === 'meeting-followup' ? calendarEvents.find((candidate) => candidate.id === item.parentId || candidate.id === item.sourceId) : null;
  const projectId = (item && item.projectId) || (meeting && meeting.projectId) || null;
  const project = projectId ? projects.find((candidate) => candidate.id === projectId) : null;
  const projectCustomerIds = project ? (project.customerIds || (project.customerId ? [project.customerId] : [])) : [];
  const customerId = (item && item.customerId) || projectCustomerIds[0] || null;
  const customer = customerId ? customers.find((candidate) => candidate.id === customerId) : null;
  const ticket = item && item.type === 'ticket' && project ? MODEL.projectTickets(project).find((candidate) => candidate.id === item.sourceId) : null;
  return { project, customer, ticket, meeting };
}

function isContextDetailItem(item) {
  return !!(item && (item.isContextItem || ['file', 'email', 'note', 'phone', 'followup'].includes(item.type)));
}

function stripHtml(html) {
  const text = String(html || '');
  if (!text) return '';
  if (typeof document === 'undefined') return text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const node = document.createElement('div');
  node.innerHTML = text;
  return (node.textContent || node.innerText || '').replace(/\s+\n/g, '\n').trim();
}

function formatFileSize(size) {
  const n = Number(size) || 0;
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function emailAttachmentIcon(attachment) {
  const name = (attachment.fileName || '').toLowerCase();
  const type = (attachment.contentType || '').toLowerCase();
  if (type.includes('pdf') || name.endsWith('.pdf')) return 'filePdf';
  if (type.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg)$/.test(name)) return 'fileImage';
  if (/\.(xls|xlsx|csv|ods)$/.test(name)) return 'fileSheet';
  if (/\.(zip|rar|7z|tar|gz)$/.test(name)) return 'fileArchive';
  return 'doc';
}

function sourceLine(label, value) {
  if (!value && value !== 0) return null;
  return <div className="cdm-source-line" key={label}><span>{label}</span><strong>{value}</strong></div>;
}

function ContextSourceDetails({ item }) {
  const [loadedEmail, setLoadedEmail] = React.useState(null);
  const [emailError, setEmailError] = React.useState('');
  const [loadingEmail, setLoadingEmail] = React.useState(false);
  const [documentPreviewUrl, setDocumentPreviewUrl] = React.useState('');
  const [documentPreviewError, setDocumentPreviewError] = React.useState('');
  const [loadingDocumentPreview, setLoadingDocumentPreview] = React.useState(false);
  const raw = (item && item.raw) || {};
  const emailSourceId = item && item.type === 'email'
    ? (item.sourceId || (String(item.id || '').startsWith('email-message:') ? String(item.id).split(':')[1] : item.id))
    : '';
  const documentHref = item && item.type === 'file' && item.projectId && item.id ? `/api/projects/${encodeURIComponent(item.projectId)}/documents/${encodeURIComponent(item.id)}` : '';
  const documentContentType = String(raw.contentType || '').toLowerCase().split(';')[0].trim();
  const documentFileName = String(raw.fileName || (item && item.title) || '').toLowerCase();
  const documentExtension = ((documentFileName.match(/\.([a-z0-9]+)$/) || [])[1] || '').trim();
  const canPreviewTextDocument = !!documentHref && (
    documentContentType.startsWith('text/') ||
    ['application/json', 'application/xml', 'application/xhtml+xml'].includes(documentContentType) ||
    ['css', 'csv', 'htm', 'html', 'js', 'json', 'jsx', 'md', 'text', 'ts', 'tsx', 'txt', 'xml'].includes(documentExtension)
  );

  React.useEffect(() => {
    let cancelled = false;
    setLoadedEmail(null);
    setEmailError('');
    setLoadingEmail(false);
    if (!item || item.type !== 'email' || !emailSourceId || !(window.API && window.API.getEmailMessage)) return () => { cancelled = true; };
    setLoadingEmail(true);
    window.API.getEmailMessage(emailSourceId)
      .then((message) => { if (!cancelled) setLoadedEmail(message); })
      .catch((error) => { if (!cancelled) setEmailError(error && error.message ? error.message : 'Could not load the full email.'); })
      .finally(() => { if (!cancelled) setLoadingEmail(false); });
    return () => { cancelled = true; };
  }, [emailSourceId, item && item.type]);

  React.useEffect(() => {
    let cancelled = false;
    let objectUrl = '';
    setDocumentPreviewUrl('');
    setDocumentPreviewError('');
    setLoadingDocumentPreview(false);
    if (!canPreviewTextDocument) return () => { cancelled = true; };
    setLoadingDocumentPreview(true);
    fetch(documentHref, { credentials: 'same-origin' })
      .then((res) => {
        if (!res.ok) throw new Error(res.status === 401 ? 'Authentication required.' : 'Could not load the document preview.');
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const fallbackContentTypes = {
          css: 'text/css',
          csv: 'text/csv',
          htm: 'text/html',
          html: 'text/html',
          js: 'text/javascript',
          json: 'application/json',
          jsx: 'text/javascript',
          md: 'text/markdown',
          text: 'text/plain',
          ts: 'text/plain',
          tsx: 'text/plain',
          txt: 'text/plain',
          xml: 'application/xml',
        };
        const previewContentType = documentContentType.startsWith('text/') || ['application/json', 'application/xml', 'application/xhtml+xml'].includes(documentContentType)
          ? documentContentType
          : fallbackContentTypes[documentExtension] || 'text/plain';
        const previewBlob = blob.type === previewContentType ? blob : blob.slice(0, blob.size, previewContentType);
        objectUrl = URL.createObjectURL(previewBlob);
        setDocumentPreviewUrl(objectUrl);
      })
      .catch((error) => { if (!cancelled) setDocumentPreviewError(error && error.message ? error.message : 'Could not load the document preview.'); })
      .finally(() => { if (!cancelled) setLoadingDocumentPreview(false); });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [canPreviewTextDocument, documentContentType, documentExtension, documentHref]);

  if (!item) return null;

  if (item.type === 'email') {
    const email = loadedEmail || raw;
    const body = email.textBody || stripHtml(email.htmlBody) || email.preview || item.body || 'No readable email body is available.';
    const attachments = email.attachments || [];
    const folder = email.folder || raw.folder;
    const uid = email.uid || raw.uid;
    return (
      <section className="cdm-source">
        <div className="cdm-source-head">
          <h3>Full email</h3>
          {loadingEmail && <span className="muted">Loading message body...</span>}
          {emailError && <span className="neg">{emailError}</span>}
        </div>
        <div className="cdm-source-meta">
          {[
            sourceLine('From', email.from || raw.from),
            sourceLine('To', email.to || raw.to),
            sourceLine('Cc', email.cc || raw.cc),
            sourceLine('Folder', email.folderName || raw.folderName || folder),
            sourceLine('Message ID', email.messageId || raw.messageId),
          ]}
        </div>
        {!!attachments.length && (
          <div className="ef-attachments">
            {attachments.map((attachment, index) => (
              <a key={`${attachment.fileName || 'attachment'}-${index}`} href={emailSourceId ? window.API.getEmailAttachmentUrl(emailSourceId, index) : '#'} download={attachment.fileName || undefined} title={`Download ${attachment.fileName || 'Attachment'}`}>
                <Icon name={emailAttachmentIcon(attachment)} size={14} />
                <strong>{attachment.fileName || 'Attachment'}</strong>
                <span>{formatFileSize(attachment.size)}</span>
              </a>
            ))}
          </div>
        )}
        <div className="ef-body-text">
          {String(body).split(/\n{2,}/).map((paragraph, index) => <p key={index}>{paragraph}</p>)}
        </div>
      </section>
    );
  }

  if (item.type === 'file') {
    const href = documentHref;
    const canPreviewImage = href && documentContentType.startsWith('image/');
    const canPreviewPdf = href && documentContentType.includes('pdf');
    return (
      <section className="cdm-source">
        <div className="cdm-source-head">
          <h3>Document source</h3>
          {href && <a className="btn sm ghost" href={href} target="_blank" rel="noreferrer"><Icon name="external" size={13} />Open file</a>}
        </div>
        <div className="cdm-source-meta">
          {[
            sourceLine('File name', raw.fileName || item.title),
            sourceLine('Content type', raw.contentType),
            sourceLine('Uploaded', raw.uploadedAt ? fmtDateTime(raw.uploadedAt) : item.sortDate && fmtDateTime(item.sortDate)),
            sourceLine('Stored file', raw.storedFileName),
          ]}
        </div>
        <div className="cdm-source-block">
          <span className="cdm-label">Remarks</span>
          <p>{raw.description || item.detail || 'No document remarks have been added.'}</p>
        </div>
        {canPreviewImage && <img className="cdm-doc-preview" src={href} alt={raw.fileName || item.title || 'Document preview'} />}
        {canPreviewPdf && <iframe className="cdm-doc-preview frame" src={href} title={raw.fileName || item.title || 'Document preview'} />}
        {canPreviewTextDocument && (
          <React.Fragment>
            {loadingDocumentPreview && <div className="cdm-source-result"><p>Loading preview...</p></div>}
            {documentPreviewError && <div className="cdm-source-result"><p className="neg">{documentPreviewError}</p></div>}
            {documentPreviewUrl && <iframe className="cdm-doc-preview frame" src={documentPreviewUrl} sandbox="" title={raw.fileName || item.title || 'Document preview'} />}
          </React.Fragment>
        )}
      </section>
    );
  }

  if (item.type === 'followup') {
    const meeting = raw.meeting || raw.event || item.meeting || {};
    return (
      <section className="cdm-source">
        <div className="cdm-source-head">
          <h3>Meeting source</h3>
        </div>
        <div className="cdm-source-result active">
          <strong>{raw.type || 'Follow-up'}</strong>
          <p>{raw.text || item.title || 'No follow-up text is available.'}</p>
        </div>
        <div className="cdm-source-meta">
          {[
            sourceLine('Meeting', meeting.title),
            sourceLine('Type', meeting.type),
            sourceLine('Status', meeting.status),
            sourceLine('Starts', meeting.startsAt ? fmtDateTime(meeting.startsAt) : meeting.date && fmtDateTime(meeting.date)),
            sourceLine('Location', meeting.location),
            sourceLine('Video link', meeting.videoLink),
          ]}
        </div>
        {!!meeting.description && <div className="cdm-source-block"><span className="cdm-label">Description</span><p>{meeting.description}</p></div>}
        <div className="cdm-source-block">
          <span className="cdm-label">Transcript</span>
          <pre className="cdm-source-text">{meeting.transcript || 'No transcript is available for this meeting.'}</pre>
        </div>
      </section>
    );
  }

  if (item.type === 'note' || item.type === 'phone') {
    return (
      <section className="cdm-source">
        <div className="cdm-source-head">
          <h3>{item.type === 'phone' ? 'Full call note' : 'Full note'}</h3>
        </div>
        <div className="cdm-source-meta">
          {[
            sourceLine('Subject', raw.subject || item.title),
            sourceLine('Type', raw.type || item.type),
            sourceLine('Date', raw.occurredAt || raw.occurredOn || raw.date ? fmtDateTime(raw.occurredAt || raw.occurredOn || raw.date) : null),
          ]}
        </div>
        <pre className="cdm-source-text">{raw.body || item.body || 'No note body is available.'}</pre>
      </section>
    );
  }

  return null;
}

function parseEmailActionId(id) {
  const parts = String(id || '').split(':');
  return parts.length === 2 ? { emailId: parts[1] } : null;
}

function actionItemEditState(item, source) {
  if (!item) return {};
  if (item.type === 'ticket') {
    const ticket = source.ticket || {};
    return { title: ticket.title || item.title || '', status: ticket.status || item.status || 'Open', done: !!ticket.done || item.status === 'Done' };
  }
  return { status: item.status || 'unprocessed' };
}

function ActionItemQuickEdit({ item, meta, form, setForm, db }) {
  const fields = meta.editFields || [];
  if (!fields.length) return <div className="cdm-edit-empty">No quick-edit fields for this item. Use Open source for the full workflow.</div>;
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  return (
    <div className="cdm-edit">
      {fields.includes('title') && <div className="field"><label>Title</label><input className="inp" value={form.title || ''} onChange={(event) => set('title', event.target.value)} /></div>}
      {fields.includes('subject') && <div className="field"><label>Subject</label><input className="inp" value={form.subject || ''} onChange={(event) => set('subject', event.target.value)} /></div>}
      {fields.includes('body') && <div className="field"><label>Body</label><textarea className="inp" rows="4" value={form.body || ''} onChange={(event) => set('body', event.target.value)} /></div>}
      {fields.includes('text') && <div className="field"><label>Follow-up text</label><textarea className="inp" rows="4" value={form.text || ''} onChange={(event) => set('text', event.target.value)} /></div>}
      <div className="cdm-edit-grid">
        {fields.includes('status') && (
          <div className="field">
            <label>Status</label>
            <select className="inp" value={form.status || 'unprocessed'} onChange={(event) => set('status', event.target.value)}>
              {(item.type === 'ticket' ? TICKET_STATUS_OPTIONS : ACTION_ITEM_STATUS_OPTIONS).map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
          </div>
        )}
        {fields.includes('type') && (
          <div className="field">
            <label>Type</label>
            <select className="inp" value={form.type || 'Action'} onChange={(event) => set('type', event.target.value)}>
              {FOLLOWUP_TYPE_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
          </div>
        )}
        {fields.includes('projectId') && (
          <div className="field">
            <label>Project</label>
            <select className="inp" value={form.projectId || ''} onChange={(event) => set('projectId', event.target.value)}>
              <option value="">No project</option>
              {(db.projects || []).map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
            </select>
          </div>
        )}
        {fields.includes('customerId') && (
          <div className="field">
            <label>Customer</label>
            <select className="inp" value={form.customerId || ''} onChange={(event) => set('customerId', event.target.value)}>
              <option value="">No customer</option>
              {(db.customers || []).map((customer) => <option key={customer.id} value={customer.id}>{customer.name}</option>)}
            </select>
          </div>
        )}
      </div>
      <div className="cdm-checks">
        {fields.includes('done') && <label className="checkrow"><input type="checkbox" checked={!!form.done} onChange={(event) => set('done', event.target.checked)} />Done</label>}
        {fields.includes('isRequirement') && <label className="checkrow"><input type="checkbox" checked={!!form.isRequirement} onChange={(event) => set('isRequirement', event.target.checked)} />Scope source</label>}
      </div>
    </div>
  );
}

async function saveActionItemQuickEdit(item, form, source) {
  if (item.type === 'ticket') {
    if (!item.projectId || !source.ticket) throw new Error('Ticket source was not found.');
    const status = form.done ? 'Done' : (form.status || 'Open');
    await window.API.updateProjectTicket(item.projectId, item.sourceId, { ...source.ticket, ticketClusterId: item.parentId || source.ticket.ticketClusterId || (source.ticket.cluster && source.ticket.cluster.id), title: form.title || '', status, done: !!form.done });
    return;
  }
  if (item.type === 'project-email') {
    if (!item.projectId) throw new Error('Project email source was not found.');
    await window.API.updateProjectEmailStatus(item.projectId, item.sourceId, form.status || 'unprocessed');
    return;
  }
  if (item.type === 'email-message') {
    const parsed = parseEmailActionId(item.id);
    if (!parsed) throw new Error('Email message source was not found.');
    await window.API.updateEmailMessageStatus(parsed.emailId, form.status || 'unprocessed');
    return;
  }
}

function ActionItemDetailModal({ item, db = {}, events, onClose, onNav, onHandle, onOpenSource, typeConfig, onToast, onError, onReload }) {
  if (!item) return null;

  const meta = actionItemMeta(item, typeConfig);
  const source = actionItemSource(db, item, events);
  const [form, setForm] = React.useState(() => actionItemEditState(item, source));
  const [saving, setSaving] = React.useState(false);
  const contextItem = isContextDetailItem(item);
  const dateValue = item.sortDate || item.date || item.messageDate || item.uploadedAt || item.occurredAt || item.occurredOn || item.attachedAt;
  const date = dateValue ? fmtDateTime(dateValue) : '-';
  React.useEffect(() => {
    setForm(actionItemEditState(item, source));
    setSaving(false);
  }, [item && item.id]);
  const openSource = () => {
    if (onOpenSource) onOpenSource(item);
    else openActionItemSource(item, onNav);
    onClose();
  };
  const handleItem = async () => {
    if (!onHandle) return;
    await onHandle(item);
    onClose();
  };
  const saveQuickEdit = async () => {
    if (saving) return;
    try {
      setSaving(true);
      await saveActionItemQuickEdit(item, form, source);
      if (onToast) onToast('Action item updated');
      if (onReload) await onReload();
      onClose();
    } catch (e) {
      if (onError) onError(e);
      else throw e;
    } finally {
      setSaving(false);
    }
  };
  const actions = [
    { key: 'close', label: 'Close', variant: 'ghost', onClick: onClose },
    { key: 'source', label: 'Open source', icon: 'external', variant: 'ghost', onClick: openSource },
  ];
  if ((meta.editFields || []).length) actions.push({ key: 'save', label: saving ? 'Saving...' : 'Save changes', icon: 'check', onClick: saveQuickEdit });
  if (onHandle) actions.push({ key: 'handle', label: 'Handle item', icon: 'check', onClick: handleItem });
  const badges = contextItem
    ? [
      item.status && <Pill kind={item.status} sm>{item.status}</Pill>,
      item.isRequirement ? <span className="pos">Scope source</span> : <span className="muted">Context</span>,
    ].filter(Boolean)
    : [
      <Pill kind={item.status || 'Open'} sm>{item.status || 'Open'}</Pill>,
      item.priority === 'High' ? <span className="neg">High priority</span> : <span className="muted">Normal priority</span>,
    ];
  const rows = [
    { label: 'Location', value: actionItemSourceLabel(item) },
    { label: 'Date', value: date },
    { label: 'Project', value: item.projectName || (source.project && source.project.name) },
    { label: 'Customer', value: item.customerName || (source.customer && source.customer.name) },
    contextItem && { label: 'Work package', value: item.workPackageName },
    contextItem && { label: 'Milestones', value: item.milestoneNames },
    { label: 'Type', value: meta.label },
    { label: 'Reference', value: <span className="mono">{item.id}</span> },
  ].filter(Boolean);
  const showQuickEdit = !contextItem || (meta.editFields || []).length > 0;
  const modalClassName = contextItem ? 'cdm-context-source-modal' : '';

  return (
    <ConfigurableDetailModal
      title={item.title || 'Untitled action item'}
      kicker={meta.label}
      subtitle={meta.headline}
      icon={meta.icon}
      tone={meta.tone}
      summaryLabel={meta.detailLabel}
      summary={item.detail || item.body || item.meta || 'No additional detail is available.'}
      badges={badges}
      rows={rows}
      actions={actions}
      className={modalClassName}
      onClose={onClose}
    >
      {showQuickEdit && <ActionItemQuickEdit item={item} meta={meta} form={form} setForm={setForm} db={db} />}
      {contextItem && <ContextSourceDetails item={item} />}
    </ConfigurableDetailModal>
  );
}

function ContextItemCard({ item, meta, routeIds = [], routeOptions = [], onOpen, onUpdateRoutes, onUpdateRequirement, onOpenSource, onEdit, onRemove, contextMenuProps, scopeActivity }) {
  const itemMeta = meta || ACTION_ITEM_DETAIL_CONFIG[item.type] || ACTION_ITEM_DETAIL_CONFIG.note;
  const stop = (handler) => (event) => {
    event.stopPropagation();
    if (handler) handler(event);
  };
  const openFromKeyboard = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    if (onOpen) onOpen(item);
  };
  return (
    <article
      className={'ctx-card type-' + item.type}
      role="button"
      tabIndex={0}
      onClick={() => onOpen && onOpen(item)}
      onKeyDown={openFromKeyboard}
      {...(contextMenuProps ? contextMenuProps(item) : {})}
    >
      <div className="ctx-card-main">
        <Icon name={itemMeta.icon} size={15} style={{ color: itemMeta.color }} />
        <span>
          <strong>{item.title}</strong>
          <small>{item.body}</small>
          <em>{item.meta}</em>
        </span>
        <span className="chip">{itemMeta.label}</span>
        <span className="chip">{item.workPackageName || 'All work packages'}</span>
        {item.status && <Pill kind={item.status} sm />}
        {scopeActivity}
        <Icon name="dots" size={14} className="faint" />
      </div>
      <div className="ctx-route-row">
        <Icon name="flag" size={13} className={routeIds.length ? 'primary' : 'warn'} />
        {routeIds.map((id) => {
          const route = routeOptions.find((candidate) => candidate.id === id);
          const title = route ? route.title : 'Milestone';
          return (
            <span className="chip primary-soft ctx-route-chip" key={id} title={title}>
              <span className="ctx-route-label">{title}</span>
              <button type="button" onClick={stop(() => onUpdateRoutes && onUpdateRoutes(item, routeIds.filter((routeId) => routeId !== id)))} title="Remove route"><Icon name="close" size={11} /></button>
            </span>
          );
        })}
        <select className={'ctx-route-select' + (routeIds.length ? '' : ' warn')} value="" onClick={stop()} onChange={stop((event) => event.target.value && onUpdateRoutes && onUpdateRoutes(item, [...routeIds, event.target.value]))}>
          <option value="">{routeIds.length ? '+ Route' : 'Route to milestone...'}</option>
          {routeOptions.filter((route) => !routeIds.includes(route.id)).map((route) => <option key={route.id} value={route.id}>{route.workPackageName} · {route.title}</option>)}
        </select>
        <span className="spacer" />
        {['file', 'email', 'note', 'phone', 'followup'].includes(item.type) && <button className={'btn sm ghost' + (item.isRequirement ? ' primary' : '')} onClick={stop(() => onUpdateRequirement && onUpdateRequirement(item))}><Icon name="check" size={12} />Req</button>}
        {(item.type === 'file' || item.type === 'email') && <button className="icon-btn" title="Open" onClick={stop(() => onOpenSource && onOpenSource(item))}><Icon name="external" size={14} /></button>}
        {(item.type === 'note' || item.type === 'phone') && <button className="icon-btn" title="Edit" onClick={stop(() => onEdit && onEdit(item))}><Icon name="edit" size={14} /></button>}
        <button className="icon-btn" title="Remove" onClick={stop(() => onRemove && onRemove(item))}><Icon name="trash" size={14} /></button>
      </div>
    </article>
  );
}

function openActionItemSource(item, onNav) {
  const openProject = (projectId, tab) => {
    window.location.hash = `projects/${encodeURIComponent(projectId)}${tab ? `/${tab}` : ''}`;
  };
  if (item.type === 'ticket' && item.projectId) openProject(item.projectId, 'board');
  else if (item.type === 'project-email' && item.projectId) openProject(item.projectId, 'email');
  else if (item.type === 'email-message') onNav('email');
  else if (item.type === 'meeting-followup' && item.projectId) openProject(item.projectId, 'calendar');
  else if (item.type === 'meeting-followup') onNav('calendar');
  else if (item.projectId) openProject(item.projectId);
}

window.Dashboard = Dashboard;
window.ACTION_ITEM_DETAIL_CONFIG = ACTION_ITEM_DETAIL_CONFIG;
window.ContextItemCard = ContextItemCard;
window.ActionItemDetailModal = ActionItemDetailModal;
window.openActionItemSource = openActionItemSource;
